import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi, useShowApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Coupon, CouponForm, CouponSearchForm } from 'entities/coupon'
import { PurchaseProduct } from 'entities/purchase_product'

type CouponsResponse = PagingResponse & {
    results: Coupon[]
}

export function useFetchCouponsApi(searchForm?: Form<CouponSearchForm>): IndexApiSet<CouponsResponse> & { execute: () => void } {
    const apiPath = 'coupons/'
    const api = useIndexApi<CouponsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export type CouponResponse = BaseResponse & Coupon

export function useFetchCouponApi(): ApiSet<CouponResponse> & { execute: (id: number) => void } {
    const api = useShowApi<CouponResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `coupons/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function useDownloadCsvCouponsApi(searchForm: Form<CouponSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'coupons/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'coupon', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostCouponApi(): ApiSet<BaseResponse> & { execute: (form: Form<CouponForm>) => void } {
    const apiSet = usePostApi<BaseResponse, CouponForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<CouponForm>) => {
        const apiPath = `coupons/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutCouponApi(): ApiSet<BaseResponse> & { execute: (form: Form<CouponForm>) => void } {
    const apiSet = usePutApi<BaseResponse, CouponForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<CouponForm>) => {
        const apiPath = `coupons/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutCouponsApi(): ApiSet<BaseResponse> & { execute: (form: Form<CouponForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, CouponForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<CouponForm[]>) => {
        const apiPath = `coupons/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteCouponApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `coupons/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export type CreateCouponCodeForm = {
    count?: number
}

export function useCreateCouponCodesApi(): ApiSet<BaseResponse> & {
    execute: (id: number, form: Form<CreateCouponCodeForm>) => void
} {
    const api = usePostApi<BaseResponse, CreateCouponCodeForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number, form: Form<CreateCouponCodeForm>): void => {
        const apiPath = `coupons/${id}/create_codes/`
        api.execute(apiPath, form)
    }

    return { ...api, execute: execute }
}

type InputCouponResponse = BaseResponse &
    Coupon & {
        purchaseProductCategories?: string[]
    }
export function useInputCouponApi(): ApiSet<InputCouponResponse> & {
    execute: (couponCode: string) => void
} {
    const api = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (couponCode: string): void => {
        const apiPath = `mypage/coupons/?coupon_code=${couponCode}`
        const isHomeTest = process.env.REACT_APP_IS_HOME_TEST
        if (!isHomeTest) {
            return
        }
        try {
            api.execute(apiPath)
        } catch (error) {
            console.error(error)
        }
    }

    return { ...api, execute: execute }
}

export function useProductsApi(): ApiSet<BaseResponse & { products: any[] }> & {
    execute: () => void
} {
    const api = useIndexApi<BaseResponse & { products: any[] }>(new CraifHttpClient(), {
        initialResponse: { products: [] },
    })

    const execute = (): void => {
        const apiPath = `mypage/products/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
